import { useState, useRef } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../auth/firebase';
import { useAuth } from '../../auth/useAuth';
import { apiCall } from '../../api/client';
import { isValidEmail } from '../../utils/phone';
import { trackSignup } from '../../utils/analytics';

export function SignupPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [name, setName] = useState('');
  const [practiceName, setPracticeName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const signingUp = useRef(false);

  // Redirect already-authenticated users to dashboard (skip during/after signup)
  if (!authLoading && user && !signingUp.current) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSignup = async () => {
    if (!name || !practiceName || !email || !password) {
      setError('All fields are required');
      return;
    }
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setError('');
    setLoading(true);
    signingUp.current = true;

    try {
      await apiCall('/auth/signup', {
        method: 'POST',
        body: { email, password, name, practiceName },
        auth: false,
      });

      await signInWithEmailAndPassword(auth, email, password);
      trackSignup(practiceName);
      navigate('/onboarding?step=practice', { replace: true });
    } catch (err: unknown) {
      signingUp.current = false;
      setError(err instanceof Error ? err.message : 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="header">
        <img src="/img/logo.svg" alt="SimplyTelehealth" style={{ width: '80px', height: '80px', marginBottom: '0.5rem' }} />
        <h1 id="practiceName">SimplyTelehealth</h1>
        <p>Telehealth made simple</p>
      </div>

      <div className="card" id="signupCard">
        <h2 className="mb-2">Create Account</h2>
        {error && <div className="alert alert--error">{error}</div>}
        <div className="form-group">
          <label htmlFor="signupName">Your Name</label>
          <input type="text" id="signupName" placeholder="Dr. Jane Smith" maxLength={200} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="form-group">
          <label htmlFor="signupPractice">Practice Name</label>
          <input type="text" id="signupPractice" placeholder="Smith Family Medicine" maxLength={200} value={practiceName} onChange={(e) => setPracticeName(e.target.value)} />
        </div>
        <div className="form-group">
          <label htmlFor="signupEmail">Email</label>
          <input type="email" id="signupEmail" placeholder="jane@smithfamilymedicine.com" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="form-group">
          <label htmlFor="signupPassword">Password</label>
          <input
            type="password"
            id="signupPassword"
            autoComplete="new-password"
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSignup(); }}
          />
        </div>
        <button className="btn btn--primary btn--full mt-1" id="signupBtn" onClick={handleSignup} disabled={loading}>
          {loading ? <><span style={{ visibility: 'hidden' }}>Create Account</span><span className="spinner" style={{ position: 'absolute' }} /></> : 'Create Account'}
        </button>
        <p className="text-center text-sm mt-2">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
